import { __extends } from "tslib";
/**
* PrivacyGuardPlugin.ts
* @author Sid Dahiya (sidahiy)
* @copyright Microsoft 2020
* File containing the implementation for Privacy Guard plugin.
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, objForEachKey, isString, isNullOrUndefined, isObject, _throwInternal } from "@microsoft/1ds-core-js";
import { PrivacyGuardInternal } from "./PrivacyGuardInternal";
var PrivacyGuardPlugin = /** @class */ (function (_super) {
    __extends(PrivacyGuardPlugin, _super);
    function PrivacyGuardPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "PrivacyGuardPlugin";
        _this.priority = 499;
        _this.version = '3.2.2';
        // Manually set configuration that is not reset during teardown / unload
        var _manualConfig = null;
        var _manualEnabled = null;
        var _manualCommonContext = null;
        // Automatic defaults set during construction and reset during teardown / unload via _initDefaults()
        var _pgInternal;
        var _core;
        var _iKey;
        var _notificationEventName;
        var _summaryEventName;
        var _useFieldPrefix;
        var _useAbsoluteFieldName;
        var _concernsNotified;
        var _eventsIgnored;
        var _eventsInspected;
        var _fieldsInspected;
        dynamicProto(PrivacyGuardPlugin, _this, function (_self, _base) {
            _initDefaults();
            _self.initialize = function (coreConfig, core, extensions) {
                _base.initialize(coreConfig, core, extensions);
                var configuration = coreConfig.extensionConfig[_self.identifier];
                if (!configuration) {
                    _throwInternal(core.logger, 1 /* CRITICAL */, 73 /* PluginException */, 'Configuration not provided, PrivacyGuard disabled.');
                    _base.setInitialized(false);
                }
                else {
                    _core = core;
                    initInternal(_manualConfig || configuration);
                    // Reapply any manually set values
                    if (!isNullOrUndefined(_manualEnabled)) {
                        _self.setEnabled(_manualEnabled);
                    }
                    if (!isNullOrUndefined(_manualCommonContext)) {
                        _self.updateCommonContext(_manualCommonContext);
                    }
                }
            };
            _self.reset = function (configuration) {
                _manualConfig = configuration;
                if (_manualConfig) { // Support "clearing" as this is not reset during unloading
                    initInternal(_manualConfig);
                }
            };
            function initInternal(configuration) {
                _pgInternal = new PrivacyGuardInternal();
                _pgInternal.initialize(configuration);
                if (configuration.notificationEventName && isString(configuration.notificationEventName)) {
                    _notificationEventName = configuration.notificationEventName;
                }
                if (configuration.summaryEventName && isString(configuration.summaryEventName)) {
                    _summaryEventName = configuration.summaryEventName;
                }
                if (configuration.iKey && isString(configuration.iKey)) {
                    _iKey = configuration.iKey;
                }
                _pgInternal.setEnabled(configuration.enabled !== false);
                _useFieldPrefix = configuration.useEventFieldPrefix !== false;
                _useAbsoluteFieldName = configuration.useAbsoluteFieldName === true;
            }
            _self.isEnabled = function () {
                return _pgInternal && _pgInternal.isEnabled();
            };
            _self.isPrivacyGuardEnabled = function () {
                return _self.isEnabled();
            };
            _self.setEnabled = function (enabled) {
                _manualEnabled = enabled;
                return _pgInternal && _pgInternal.setEnabled(enabled);
            };
            _self.updateCommonContext = function (freshCommonContext) {
                _manualCommonContext = freshCommonContext;
                if (_pgInternal) {
                    _pgInternal.updateCommonContext(_manualCommonContext);
                }
            };
            _self.processTelemetry = function (event, itemCtx) {
                if (_self.isEnabled() && event.name !== _notificationEventName && event.name !== _summaryEventName) {
                    if (event.data) {
                        _eventsInspected++;
                        var baseType;
                        // Office Telemetry JS SDK uses event.baseType, while CS 2.0 used event.data.baseType.
                        if (isString(event.baseType)) {
                            baseType = event.baseType;
                        }
                        else if (event.data && event.data.baseType && isString(event.data.baseType)) {
                            baseType = event.data.baseType;
                        }
                        var concerns = processObject(event.name, baseType, "", event.data);
                        if (concerns.length > 0) {
                            _self.notifyConcerns(concerns);
                        }
                    }
                    else {
                        // Account for any event who's data might be empty.
                        // We do not count Summary and Notification event in this metric.
                        _eventsIgnored++;
                    }
                }
                _self.processNext(event, itemCtx);
            };
            function processObject(eventName, baseTypeName, parentKey, toProcess) {
                if (!_self.isEnabled()) {
                    return [];
                }
                var concerns = [];
                if (isNullOrUndefined(toProcess)) {
                    return concerns;
                }
                objForEachKey(toProcess, function (propKey, theValue) {
                    var fieldName = propKey;
                    if (_useAbsoluteFieldName && parentKey && parentKey.length > 0) {
                        fieldName = parentKey + "." + propKey;
                    }
                    if (isString(theValue)) {
                        _fieldsInspected++;
                        concerns = concerns.concat(_pgInternal.getAllPrivacyConcerns(eventName, baseTypeName, fieldName, theValue));
                    }
                    else if (theValue && isObject(theValue)) {
                        concerns = concerns.concat(processObject(eventName, baseTypeName, fieldName, theValue));
                    }
                });
                return concerns;
            }
            _self.sendSummaryEvent = function (shouldResetSummary) {
                var pgEvent = {
                    name: _summaryEventName,
                    ext: {
                        metadata: {
                            privTags: 33554432,
                            privLevel: 110
                        }
                    }
                };
                if (_useFieldPrefix) {
                    pgEvent.data = {
                        PG_ConcernsNotified: _concernsNotified,
                        PG_EventsIgnored: _eventsIgnored,
                        PG_EventsInspected: _eventsInspected,
                        PG_FieldsInspected: _fieldsInspected
                    };
                }
                else {
                    pgEvent.data = {
                        ConcernsNotified: _concernsNotified,
                        EventsIgnored: _eventsIgnored,
                        EventsInspected: _eventsInspected,
                        FieldsInspected: _fieldsInspected
                    };
                }
                if (_iKey) {
                    pgEvent.iKey = _iKey;
                }
                _core.track(pgEvent);
                if (shouldResetSummary) {
                    _concernsNotified = 0;
                    _eventsIgnored = 0;
                    _eventsInspected = 0;
                    _fieldsInspected = 0;
                }
            };
            _self.notifyConcerns = function (concerns) {
                for (var _i = 0, concerns_1 = concerns; _i < concerns_1.length; _i++) {
                    var concern = concerns_1[_i];
                    var pgEvent = {
                        name: _notificationEventName,
                        ext: {
                            metadata: {
                                privTags: 33554432,
                                privLevel: 110
                            }
                        }
                    };
                    if (_useFieldPrefix) {
                        pgEvent.data = {
                            PG_EventName: concern.EventName,
                            PG_FieldName: concern.FieldName,
                            PG_TypeAsText: DataConcernTypeToString(concern.Concern),
                            PG_BaseType: concern.BaseType
                        };
                    }
                    else {
                        pgEvent.data = {
                            EventName: concern.EventName,
                            FieldName: concern.FieldName,
                            TypeAsText: DataConcernTypeToString(concern.Concern),
                            BaseType: concern.BaseType
                        };
                    }
                    if (_iKey) {
                        pgEvent.iKey = _iKey;
                    }
                    _core.track(pgEvent);
                    _concernsNotified++;
                }
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _initDefaults();
            };
            function _initDefaults() {
                _pgInternal = null;
                _core = null;
                _iKey = null;
                _notificationEventName = "PrivacyConcern";
                _summaryEventName = "PrivacyGuardSummary";
                _useFieldPrefix = true;
                _useAbsoluteFieldName = false;
                _concernsNotified = 0;
                _eventsIgnored = 0;
                _eventsInspected = 0;
                _fieldsInspected = 0;
            }
            function DataConcernTypeToString(type) {
                {
                    switch (type) {
                        case 0 /* None */:
                            return "None";
                        case 6 /* FieldNameImpliesLocation */:
                            return "FieldNameImpliesLocation";
                        case 11 /* InternalEmailAddress */:
                            return "InternalEmailAddress";
                        case 15 /* OutOfScopeIdentifier */:
                            return "OutOfScopeIdentifier";
                        case 14 /* MachineName */:
                            return "MachineName";
                        case 20 /* UserDomain */:
                            return "UserDomain";
                        case 12 /* IpAddress */:
                            return "IPAddress";
                        case 5 /* ExternalEmailAddress */:
                            return "ExternalEmailAddress";
                        case 21 /* UserName */:
                            return "UserName";
                        case 19 /* UserAlias */:
                            return "UserAlias";
                        case 4 /* Directory */:
                            return "Directory";
                        case 18 /* Url */:
                            return "Url";
                        case 7 /* FileNameOrExtension */:
                            return "FileNameOrExtension";
                        case 1 /* Content */:
                            return "Content";
                        case 8 /* FileSharingUrl */:
                            return "FileSharingUrl";
                        case 17 /* Security */:
                            return "Security";
                        case 22 /* AccessToken */:
                            return "AccessToken";
                        case 23 /* Signature */:
                            return "Signature";
                    }
                    return "Missing String value for " + type;
                }
            }
        });
        return _this;
    }
    /**
     * Starts the Privacy Guard plugin
     * @param config The core configuration.
     */
    PrivacyGuardPlugin.prototype.initialize = function (coreConfig, core, extensions) {
        // Make sure we have a config definition for this identifier
    };
    /**
     * Process the event and add it to an internal queue if handshake in process
     * @param event - The event to process
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    PrivacyGuardPlugin.prototype.processTelemetry = function (event, itemCtx) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    PrivacyGuardPlugin.prototype.updateCommonContext = function (ctx) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return null;
    };
    PrivacyGuardPlugin.prototype.isEnabled = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return false;
    };
    /** @deprecated - Use `isEnabled` */
    PrivacyGuardPlugin.prototype.isPrivacyGuardEnabled = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return false;
    };
    PrivacyGuardPlugin.prototype.setEnabled = function (enabled) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
        return false;
    };
    PrivacyGuardPlugin.prototype.sendSummaryEvent = function (shouldResetSummary) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    PrivacyGuardPlugin.prototype.reset = function (configuration) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    PrivacyGuardPlugin.prototype.notifyConcerns = function (concerns) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    return PrivacyGuardPlugin;
}(BaseTelemetryPlugin));
export { PrivacyGuardPlugin };
//# sourceMappingURL=PrivacyGuardPlugin.js.map